/**
 * WizardMode Container
 * Main container that orchestrates the wizard flow
 */
import React, { useCallback, useRef, useState, useMemo } from 'react';
import WizardStepper from './WizardStepper';
import WizardUpload from './WizardUpload';
import WizardPreview from './WizardPreview';
import WizardChunking from './WizardChunking';
import WizardGenerate from './WizardGenerate';
import { useWizardState, WIZARD_STEPS, CHUNKING_STRATEGIES } from './hooks/useWizardState';
import { usePdfParser } from './hooks/usePdfParser';
import { supportsVision, callOpenAIWithImages, callAnthropicWithImages, callOpenRouterWithImages, callGroqWithImages } from '../../../utils/llm-generator/apiClients';
import { convertPdfToImages } from '../../../utils/llm-generator/fileParser';
import { Logger } from '../../../utils/logger';

const log = Logger.create('WizardMode');

function WizardMode({ settings, onModeToggle, onExportQuestions }) {
  // Generation settings state (like QuickSettings)
  const [numberOfQuestions, setNumberOfQuestions] = useState(10);
  const [difficulty, setDifficulty] = useState('medium');
  const [includeCodeBlocks, setIncludeCodeBlocks] = useState(true);
  const [multipleChoiceMode, setMultipleChoiceMode] = useState('single');
  const [userGuidance, setUserGuidance] = useState('');
  const [topicTags, setTopicTags] = useState([]);
  const [currentTag, setCurrentTag] = useState('');
  
  // PDF processing mode: 'local' (extract text), 'native' (API), 'images' (vision)
  const [pdfProcessingMode, setPdfProcessingMode] = useState('local');
  const [imageQuality, setImageQuality] = useState('medium');
  
  // Topic tag handlers
  const handleAddTag = useCallback(() => {
    if (currentTag.trim() && !topicTags.includes(currentTag.trim())) {
      setTopicTags(prev => [...prev, currentTag.trim()]);
      setCurrentTag('');
    }
  }, [currentTag, topicTags]);
  
  const handleRemoveTag = useCallback((tagToRemove) => {
    setTopicTags(prev => prev.filter(tag => tag !== tagToRemove));
  }, []);
  const {
    currentStep,
    files,
    chunks,
    results,
    currentChunkIndex,
    isGenerating,
    chunkingStrategy,
    blockSize,
    goToStep,
    addFile,
    removeFile,
    togglePageSelection,
    selectAllPages,
    selectPageRange,
    updatePageText,
    setChunkingStrategy,
    setBlockSize,
    generateChunks,
    startGeneration,
    completeChunk,
    setIsGenerating
  } = useWizardState();
  
  const { parsePdfFile, parseTextFile, isLoading: isParsing } = usePdfParser();
  const abortControllerRef = useRef(null);
  
  // Calculate totals
  const totalPages = files.reduce((sum, f) => sum + f.totalPages, 0);
  const selectedPages = files.reduce((sum, f) => 
    sum + f.pages.filter(p => p.selected).length, 0
  );
  
  // Check if we have PDF files
  const hasPdfFiles = useMemo(() => 
    files.some(f => f.name.toLowerCase().endsWith('.pdf')), 
    [files]
  );
  
  // Check if current model supports vision
  const modelSupportsVision = useMemo(() => 
    supportsVision(settings?.model || '', settings?.provider || ''),
    [settings?.model, settings?.provider]
  );
  
  // Handle file upload
  const handleFilesAdded = useCallback(async (newFiles) => {
    for (const file of newFiles) {
      const isPdf = file.name.toLowerCase().endsWith('.pdf');
      const isTxt = file.name.toLowerCase().endsWith('.txt') || file.name.toLowerCase().endsWith('.md');
      
      if (isPdf) {
        const parsed = await parsePdfFile(file);
        if (parsed) {
          addFile({
            id: Date.now() + Math.random(),
            name: file.name,
            file: file,
            size: file.size, // Add file size for display
            totalPages: parsed.numPages,
            pages: parsed.pages // Already in correct format from parsePdfFileInternal
          });
        }
      } else if (isTxt) {
        const text = await parseTextFile(file);
        if (text) {
          addFile({
            id: Date.now() + Math.random(),
            name: file.name,
            file: file,
            size: file.size, // Add file size for display
            totalPages: 1,
            pages: [{
              pageNumber: 1,
              text,
              selected: true,
              edited: false
            }]
          });
        }
      }
    }
  }, [parsePdfFile, parseTextFile, addFile]);
  
  // Handle select all for a file
  const handleSelectAll = useCallback((fileId, selected) => {
    selectAllPages(fileId, selected);
  }, [selectAllPages]);
  
  // Generate chunks based on strategy
  const handleGenerateChunks = useCallback(() => {
    generateChunks();
  }, [generateChunks]);
  
  // Build dynamic prompt based on settings
  const buildDynamicPrompt = useCallback(() => {
    let basePrompt = `Erstelle ${numberOfQuestions} Quiz-Fragen im JSON-Format.`;
    
    // Add difficulty
    const difficultyText = {
      easy: 'Die Fragen sollen einfach sein.',
      medium: 'Die Fragen sollen mittelschwer sein.',
      hard: 'Die Fragen sollen schwer und anspruchsvoll sein.',
      mixed: 'Mische verschiedene Schwierigkeitsgrade.'
    };
    basePrompt += ' ' + (difficultyText[difficulty] || difficultyText.medium);
    
    // Add answer type
    if (multipleChoiceMode === 'single') {
      basePrompt += ' Jede Frage soll genau eine richtige Antwort haben (Single Choice).';
    } else if (multipleChoiceMode === 'multiple') {
      basePrompt += ' Die Fragen können mehrere richtige Antworten haben (Multiple Choice).';
    } else {
      basePrompt += ' Mische Single Choice und Multiple Choice Fragen.';
    }
    
    // Add code blocks preference
    if (includeCodeBlocks) {
      basePrompt += ' Füge wo passend Code-Beispiele hinzu.';
    } else {
      basePrompt += ' Verwende keine Code-Beispiele.';
    }
    
    // Add topic tags
    if (topicTags.length > 0) {
      basePrompt += ` Fokussiere auf folgende Themen: ${topicTags.join(', ')}.`;
    }
    
    // Add user guidance
    if (userGuidance.trim()) {
      basePrompt += ` Zusätzliche Anweisungen: ${userGuidance}`;
    }
    
    basePrompt += '\n\nAntworte mit einem JSON-Array von Fragen im folgenden Format:\n[{"question": "...", "options": ["A", "B", "C", "D"], "correctAnswers": [0], "type": "mc", "explanation": "..."}]';
    
    return basePrompt;
  }, [numberOfQuestions, difficulty, multipleChoiceMode, includeCodeBlocks, topicTags, userGuidance]);
  
  // Helper to parse JSON from LLM response
  const parseQuestionsFromResponse = useCallback((content) => {
    let questions = [];
    try {
      // Try to extract JSON from markdown code blocks
      const jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (jsonMatch) {
        questions = JSON.parse(jsonMatch[1]);
      } else {
        // Try to parse the whole content
        questions = JSON.parse(content);
      }
      
      // Normalize to array
      if (!Array.isArray(questions)) {
        questions = questions.questions || questions.items || [questions];
      }
    } catch (parseError) {
      log.error('Failed to parse questions', parseError);
      throw new Error('Konnte JSON nicht parsen');
    }
    return questions;
  }, []);
  
  // Start generation process
  const handleStartGeneration = useCallback(async () => {
    const dynamicPrompt = buildDynamicPrompt();
    if (chunks.length === 0) return;
    
    abortControllerRef.current = new AbortController();
    startGeneration();
    
    const systemPrompt = 'Du bist ein Experte für die Erstellung von Quiz-Fragen. Antworte immer im JSON-Format.';
    
    // Process each chunk sequentially
    for (let i = currentChunkIndex; i < chunks.length; i++) {
      if (abortControllerRef.current?.signal.aborted) break;
      
      const chunk = chunks[i];
      
      try {
        let content = '';
        
        // Check if we should send PDF as images (vision mode)
        if (pdfProcessingMode === 'images' && hasPdfFiles && modelSupportsVision) {
          // Get the pages from this chunk
          const pageNumbers = chunk.pages.map(p => p.pageNumber);
          
          // Find the PDF file(s) for this chunk
          const pdfFiles = files.filter(f => 
            f.name.toLowerCase().endsWith('.pdf') && 
            chunk.pages.some(p => p.fileId === f.id)
          );
          
          if (pdfFiles.length > 0) {
            // Convert PDF pages to images
            const allImages = [];
            for (const pdfFile of pdfFiles) {
              const relevantPageNumbers = chunk.pages
                .filter(p => p.fileId === pdfFile.id)
                .map(p => p.pageNumber);
              
              if (relevantPageNumbers.length > 0) {
                const imageResult = await convertPdfToImages(pdfFile.file, {
                  quality: imageQuality,
                  pageNumbers: relevantPageNumbers
                });
                allImages.push(...imageResult.images);
              }
            }
            
            // Call vision API
            const userPrompt = `${dynamicPrompt}\n\n---\nAnalysiere die folgenden ${allImages.length} Seiten und erstelle Quiz-Fragen basierend auf dem Inhalt.`;
            
            if (settings.provider === 'anthropic') {
              content = await callAnthropicWithImages(
                systemPrompt,
                userPrompt,
                allImages,
                settings.apiKey,
                settings.model,
                settings.baseUrl,
                settings.temperature || 0.7,
                settings.maxTokens || 4000
              );
            } else if (settings.provider === 'openrouter') {
              // OpenRouter with vision
              content = await callOpenRouterWithImages(
                systemPrompt,
                userPrompt,
                allImages,
                settings.apiKey,
                settings.model,
                settings.temperature || 0.7,
                settings.maxTokens || 4000,
                imageQuality // 'low', 'medium', 'high' -> maps to 'low', 'auto', 'high'
              );
            } else if (settings.provider === 'groq') {
              // Groq with vision (Llama 4 Scout/Maverick)
              content = await callGroqWithImages(
                systemPrompt,
                userPrompt,
                allImages,
                settings.apiKey,
                settings.model,
                settings.temperature || 0.7,
                settings.maxTokens || 4000
              );
            } else {
              // OpenAI
              content = await callOpenAIWithImages(
                systemPrompt,
                userPrompt,
                allImages,
                settings.apiKey,
                settings.model,
                settings.baseUrl || 'https://api.openai.com/v1/chat/completions',
                settings.temperature || 0.7,
                settings.maxTokens || 4000,
                imageQuality // 'low', 'medium', 'high' -> maps to 'low', 'auto', 'high'
              );
            }
          }
        }
        
        // Fallback to text-based generation if no images or not enabled
        if (!content) {
          // Original text-based approach
          const requestBody = {
            model: settings.model,
            messages: [
              { role: 'system', content: systemPrompt },
              { role: 'user', content: `${dynamicPrompt}\n\n---\nQuelltext:\n${chunk.text}` }
            ],
            temperature: settings.temperature || 0.7,
            max_tokens: settings.maxTokens || 4000
          };
          
          // Determine API endpoint
          let apiUrl = '';
          let headers = { 'Content-Type': 'application/json' };
          
          if (settings.provider === 'openrouter') {
            apiUrl = 'https://openrouter.ai/api/v1/chat/completions';
            headers['Authorization'] = `Bearer ${settings.apiKey}`;
            headers['HTTP-Referer'] = window.location.origin;
          } else if (settings.provider === 'openai') {
            apiUrl = 'https://api.openai.com/v1/chat/completions';
            headers['Authorization'] = `Bearer ${settings.apiKey}`;
          } else if (settings.provider === 'ollama') {
            apiUrl = `${settings.ollamaUrl || 'http://localhost:11434'}/api/chat`;
            requestBody.stream = false;
          }
          
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(requestBody),
            signal: abortControllerRef.current?.signal
          });
          
          if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
          }
          
          const data = await response.json();
          
          // Extract content
          if (settings.provider === 'ollama') {
            content = data.message?.content || '';
          } else {
            content = data.choices?.[0]?.message?.content || '';
          }
        }
        
        // Parse questions from response
        const questions = parseQuestionsFromResponse(content);
        completeChunk(i, true, questions);
        
      } catch (error) {
        if (error.name === 'AbortError') {
          log.debug('Generation aborted');
          break;
        }
        log.error(`Error processing chunk ${i}`, error);
        completeChunk(i, false, null, error.message);
      }
    }
    
    setIsGenerating(false);
  }, [buildDynamicPrompt, chunks, currentChunkIndex, settings, startGeneration, completeChunk, setIsGenerating, pdfProcessingMode, hasPdfFiles, modelSupportsVision, imageQuality, files, parseQuestionsFromResponse]);
  
  // Stop generation
  const handleStopGeneration = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsGenerating(false);
  }, [setIsGenerating]);
  
  // Export results
  const handleExport = useCallback(() => {
    const allQuestions = results
      .filter(r => r?.success && r?.questions)
      .flatMap(r => r.questions);
    
    if (onExportQuestions) {
      onExportQuestions(allQuestions);
    } else {
      // Default: download as JSON
      const blob = new Blob([JSON.stringify(allQuestions, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quiz-questions-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }, [results, onExportQuestions]);
  
  // Navigation handlers
  const handlePrev = useCallback(() => {
    if (currentStep > WIZARD_STEPS.UPLOAD) {
      goToStep(currentStep - 1);
    }
  }, [currentStep, goToStep]);
  
  const handleNext = useCallback(() => {
    if (currentStep < WIZARD_STEPS.GENERATE) {
      goToStep(currentStep + 1);
    }
  }, [currentStep, goToStep]);
  
  return (
    <div className="space-y-6">
      {/* Stepper */}
      <WizardStepper
        currentStep={currentStep}
        onStepClick={goToStep}
        completedSteps={{
          [WIZARD_STEPS.UPLOAD]: files.length > 0,
          [WIZARD_STEPS.PREVIEW]: selectedPages > 0,
          [WIZARD_STEPS.CHUNKING]: chunks.length > 0,
          [WIZARD_STEPS.GENERATE]: results.some(r => r?.success)
        }}
      />
      
      {/* Step Content */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        {currentStep === WIZARD_STEPS.UPLOAD && (
          <WizardUpload
            files={files}
            onFilesAdded={handleFilesAdded}
            onRemoveFile={removeFile}
            isParsing={isParsing}
            onNext={handleNext}
          />
        )}
        
        {currentStep === WIZARD_STEPS.PREVIEW && (
          <WizardPreview
            files={files}
            selectedPageCount={selectedPages}
            totalPageCount={totalPages}
            onTogglePage={togglePageSelection}
            onSelectAll={handleSelectAll}
            onSelectRange={selectPageRange}
            onUpdatePageText={updatePageText}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        )}
        
        {currentStep === WIZARD_STEPS.CHUNKING && (
          <WizardChunking
            files={files}
            chunkingStrategy={chunkingStrategy}
            blockSize={blockSize}
            onStrategyChange={setChunkingStrategy}
            onBlockSizeChange={setBlockSize}
            onGenerateChunks={handleGenerateChunks}
            chunks={chunks}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        )}
        
        {currentStep === WIZARD_STEPS.GENERATE && (
          <WizardGenerate
            chunks={chunks}
            currentChunkIndex={currentChunkIndex}
            results={results}
            isGenerating={isGenerating}
            onStartGeneration={handleStartGeneration}
            onStopGeneration={handleStopGeneration}
            onExport={handleExport}
            onPrev={handlePrev}
            totalQuestions={results.filter(r => r?.success).flatMap(r => r.questions || []).length}
            // QuickSettings props
            numberOfQuestions={numberOfQuestions}
            setNumberOfQuestions={setNumberOfQuestions}
            difficulty={difficulty}
            setDifficulty={setDifficulty}
            includeCodeBlocks={includeCodeBlocks}
            setIncludeCodeBlocks={setIncludeCodeBlocks}
            multipleChoiceMode={multipleChoiceMode}
            setMultipleChoiceMode={setMultipleChoiceMode}
            userGuidance={userGuidance}
            setUserGuidance={setUserGuidance}
            topicTags={topicTags}
            currentTag={currentTag}
            setCurrentTag={setCurrentTag}
            onAddTopicTag={handleAddTag}
            onRemoveTopicTag={handleRemoveTag}
            // PDF processing mode props
            pdfProcessingMode={pdfProcessingMode}
            setPdfProcessingMode={setPdfProcessingMode}
            imageQuality={imageQuality}
            setImageQuality={setImageQuality}
            hasPdfFiles={hasPdfFiles}
            supportsVision={modelSupportsVision}
            llmProvider={settings?.provider}
          />
        )}
      </div>
    </div>
  );
}

export default WizardMode;
